import { useCallback, useMemo } from 'react';
import { BaseOption } from 'ui/selectv2/types';
import { isEmptyString } from 'utils/stringHelpers';
import { useMovableInkCampaignContext } from '../../../contexts/MovableInkCampaignContext';
import { useTranslations } from '../../../hooks/useTranslations';
import { useInternalPaginationContext } from '../../../contexts/InternalPagination';
import { usePanelContext } from '../../../contexts/Panel';

export const useCampaignOptionsState = () => {
  const { t } = useTranslations();
  const {
    campaigns,
    campaignId,
    isFetchingCampaigns,
    setCampaignId,
    hasCampaignsError,
    hasCampaigns,
  } = useMovableInkCampaignContext();

  const { setCurrentPage } = useInternalPaginationContext();
  const { setPanelSearch } = usePanelContext();

  const allCampaignsOption: BaseOption = {
    label: t('select.campaign.defaultOption.label'),
    value: '',
  };

  const options: BaseOption<string>[] = [
    allCampaignsOption,
    ...campaigns.map(({ id, name }) => ({
      label: name,
      value: id.toString(),
      ...(name.length > 35 ? { tooltipContent: name } : {}),
    })),
  ];

  const selectedOption = useMemo(() => {
    if (isFetchingCampaigns) return;
    else
      return options.find(({ value }) =>
        campaignId ? parseInt(value, 10) === campaignId : value === '',
      );
  }, [campaigns, campaignId, isFetchingCampaigns]);

  const isDisabled = useMemo(
    () => !isFetchingCampaigns && campaigns.length === 0,
    [isFetchingCampaigns, campaigns],
  );

  const onSelect = useCallback(
    ([{ value }]: BaseOption<string>[]) => {
      setCurrentPage(1);
      setPanelSearch('');
      setCampaignId(isEmptyString(value) ? undefined : parseInt(value, 10));
    },
    [setCurrentPage, setCampaignId, setPanelSearch],
  );

  return {
    options,
    selectedOption,
    isFetchingCampaigns,
    onSelect,
    hasCampaignsError,
    isDisabled,
    hasCampaigns,
  };
};
